import { DashboardState, ESdChangeReason, LeftPanelType, ViewPart, Visibility } from "@kuika/kuika-cl-model";
import { Resizable } from "re-resizable";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import {
  clampLeftWidth,
  CSS_VAR_LEFT,
  getLeftPanelMaxWidth,
  LEFT_MIN_WIDTH,
  PANEL_MARGIN_BUFFER_PX,
  readStoredLeftWidth,
  readRightPanelWidth,
  setCssVarPx,
  supportsLeftPanelResize,
  writeStoredLeftWidth
} from "../../../shared/utility/panel-dimensions";
import { ManagementLeftPanel } from "../../management/left-panel/management-left-panel";
import KuikaButton from "../../ui-kit/kuika-button/kuika-button";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { ActionTreePanel } from "./action-tree-panel/action-tree-panel";
import { AuditWindow } from "./audit-panel/audit-window";
import { CommentsPanel } from "./comments-panel/comments-panel";
import { ComponentTree } from "./component-tree-panel/component-tree";
import { ComponentList } from "./components-panel/component-list";
import { DataDiagramSchemeWindow } from "./datadiagramscheme-panel/data-diagram-scheme-window";
import "./design-left-panel.scss";
import { DataSources } from "./datasource-panel/datasources";
import { LanguageWindow } from "./language-panel/language-window";
import { ScreensWindow } from "./screens-panel/screens-window";
import { WorkFlowFieldsPanel } from "./work-flow-list/work-flow-fields-panel";
import { ZendeskPanel } from "./zendesk-panel/zendesk-panel";

export interface IDesignLeftPanelState {
  panelWidth: number;
  isResizing: boolean;
}

export interface IDesignLeftPanelProps {}

class DesignLeftPanel extends PureSdComponent<
  IDesignLeftPanelProps & IWithSDProps & IWithContextProp,
  IDesignLeftPanelState
> {
  private minPanelWidth = LEFT_MIN_WIDTH;

  // Opens at its own floor, so every untouched tool looks the same.
  private defaultPanelWidth = LEFT_MIN_WIDTH;

  /** Re-deriving only when the ceiling MOVES is what keeps this out of a drag's way — the
   *  stored width lags the gesture by a frame and would fight the pointer. */
  private lastMaxWidth: number = 0;

  constructor(props: IDesignLeftPanelProps & IWithSDProps & IWithContextProp) {
    super(props);
    this.state = {
      panelWidth: this.defaultPanelWidth,
      isResizing: false
    };
  }

  /**
   * State plus the CSS var the canvas, sub-bar and header measure against. Always clamped.
   * Does NOT store: a tool switch or a clamp must never overwrite a width the user picked.
   */
  private applyPanelWidth = (px: number): number => {
    const width: number = clampLeftWidth(px, this.props.sd);
    if (width !== this.state.panelWidth) {
      this.setState({ panelWidth: width });
    }
    setCssVarPx(CSS_VAR_LEFT, width);
    return width;
  };

  /** Apply and store. Only user gestures commit. */
  private commitPanelWidth = (px: number): number => {
    const width: number = this.applyPanelWidth(px);
    writeStoredLeftWidth(this.props.sd.dashboardState, width);
    return width;
  };

  private resolvePanelWidth = (state: DashboardState): number => {
    const remembered: number | null = readStoredLeftWidth(state);
    return remembered ?? this.defaultPanelWidth;
  };

  componentDidMount() {
    // Collapsing UNMOUNTS this panel, so mount is also the expand path — which is why every
    // mutation goes through apply/commit rather than writing state and the var by hand.
    this.applyPanelWidth(this.resolvePanelWidth(this.props.sd.dashboardState));
    this.lastMaxWidth = getLeftPanelMaxWidth(this.props.sd);
  }

  componentDidUpdate(prevProps: Readonly<IDesignLeftPanelProps & IWithSDProps & IWithContextProp>) {
    const prevState: DashboardState = prevProps.sd.dashboardState;
    const currState: DashboardState = this.props.sd.dashboardState;

    if (prevState !== currState) {
      this.applyPanelWidth(this.resolvePanelWidth(currState));
      this.lastMaxWidth = getLeftPanelMaxWidth(this.props.sd);
      return;
    }

    // A drag owns the width until it lets go.
    if (this.state.isResizing) return;

    // Re-derive from the STORED width, never the on-screen one: that is what lets the panel
    // shrink to fit and get its full width back once there is room again.
    const maxWidth: number = getLeftPanelMaxWidth(this.props.sd);
    if (maxWidth !== this.lastMaxWidth) {
      this.lastMaxWidth = maxWidth;
      this.applyPanelWidth(this.resolvePanelWidth(currState));
    }
  }

  componentWillUnmount() {}

  getMaxPanelWidth = (): number => getLeftPanelMaxWidth(this.props.sd);

  handleResizeStart = () => {
    this.setState({ isResizing: true });
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  adjustCanvasIfNeeded = (newLeftWidth: number): void => {
    const kuika_designArea = document.getElementById("kuika_designArea");
    const kuika_iframeWrapper = document.getElementsByClassName("kuika_iframeWrapper")[0] as HTMLElement;
    const kuika_iframeAgent_canvas = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement;

    if (!kuika_designArea || !kuika_iframeWrapper) {
      return;
    }

    // Only the views that can be dragged get their canvas adjusted. The widths written below
    // come from `screen.width`, which on a report screen is not the page size — the paper is —
    // so running this there would put a stale device width on the design area and the frame.
    if (!supportsLeftPanelResize(this.props.sd.dashboardState)) {
      return;
    }

    const rightPanelWidth = this.props.sd.isRightPanelCollapsed?.[this.props.sd.dashboardState]
      ? 50
      : readRightPanelWidth();

    const viewportWidth = window.innerWidth;
    const availableWrapperWidth = viewportWidth - newLeftWidth - rightPanelWidth - PANEL_MARGIN_BUFFER_PX;

    const paddingBuffer = 24;
    const maxCanvasFitWidth = availableWrapperWidth - paddingBuffer;

    const screen = this.props.sd.project?.screenCanvasSizes?.screens?.find((s) => s.id === this.props.sd.sdId);
    const preferredWidth = screen?.width;

    if (preferredWidth) {
      const minCanvasWidth = 320;
      const newCanvasWidth = Math.max(minCanvasWidth, Math.min(preferredWidth, maxCanvasFitWidth));
      kuika_designArea.style.width = `${newCanvasWidth}px`;
      if (kuika_iframeAgent_canvas) {
        kuika_iframeAgent_canvas.style.width = `${newCanvasWidth}px`;
      }
    } else {
      kuika_designArea.style.width = `100%`;
      if (kuika_iframeAgent_canvas) {
        kuika_iframeAgent_canvas.style.width = `${maxCanvasFitWidth}px`;
      }
    }
  };

  handleResize = (e, direction, ref) => {
    const newWidth: number = clampLeftWidth(ref.offsetWidth, this.props.sd);
    if (newWidth === this.state.panelWidth) return;

    this.adjustCanvasIfNeeded(newWidth);
    this.commitPanelWidth(newWidth);

    const display =
      this.props.sd.dashboardState === DashboardState.design ||
      this.props.sd.dashboardState === DashboardState.reportDesigner;
    KMainFunctions.waitAndResize(display, this.props.sd.toolbarIsHidden, this.props.sd);
  };

  handleResizeStop = () => {
    this.setState({ isResizing: false });
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  /** The sash gesture from VS Code: without a way back, a widened panel reads as stuck. */
  handleResizeReset = (e: React.MouseEvent) => {
    e.stopPropagation();
    this.commitPanelWidth(this.defaultPanelWidth);
  };

  handleResizeKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    const step: number = e.shiftKey ? 50 : 10;
    const next: number = e.key === "ArrowRight" ? this.state.panelWidth + step : this.state.panelWidth - step;
    this.commitPanelWidth(next);
  };

  /** Collapse control pinned to the panel's right edge. Rendered inside the resize handle
   *  where dragging is offered, and standalone where it isn't (Report Designer), so a view
   *  never loses collapse just because it can't be resized. */
  private renderEdgeCollapse = (): JSX.Element => (
    // Both are stopped because this control sits INSIDE the resize handle: its drag would start
    // on mousedown, and its double-click would reset the width the user set.
    <div
      className="kuika-left-panel__edge-collapse"
      onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
      onDoubleClick={(e: React.MouseEvent) => e.stopPropagation()}
    >
      <KuikaButton
        type="secondary"
        size="md"
        isIcon
        aria-label="Collapse panel"
        style={{ boxShadow: "var(--shadow-sm)" }}
        onClick={(e?: React.MouseEvent) => {
          e?.stopPropagation();
          // Not setLeftPanelCollapsed directly: on the workflow editor the collapse map is not
          // what hides this panel, so the flag did nothing there. The provider routes it.
          this.props.sd.collapseLeftPanel();
        }}
      >
        <KuikaSvg name="layout-sidebar-left-collapse" type="sidebar-icons" width={16} height={16} pointer />
      </KuikaButton>
    </div>
  );

  render() {
    return (
      <>
        {this.props.sd.getVisibility(ViewPart.LeftPanel) === Visibility.Visible && (
          <>
            <Resizable
              className="kuika-left-panel"
              size={{ width: this.state.panelWidth, height: "100%" }}
              minWidth={this.minPanelWidth}
              maxWidth={this.getMaxPanelWidth()}
              enable={{ right: supportsLeftPanelResize(this.props.sd.dashboardState) }}
              handleComponent={
                supportsLeftPanelResize(this.props.sd.dashboardState)
                  ? {
                      right: (
                        <div
                          className={`kuika-left-panel__resize-handle ${
                            this.state.isResizing ? "kuika-left-panel__resize-handle--resizing" : ""
                          }`}
                          role="separator"
                          aria-orientation="vertical"
                          aria-label="Resize left panel (double-click to reset its width)"
                          title="Drag to resize · double-click to reset"
                          tabIndex={0}
                          onKeyDown={this.handleResizeKeyDown}
                          onDoubleClick={this.handleResizeReset}
                          style={{
                            cursor: this.state.isResizing ? "col-resize" : "ew-resize"
                          }}
                        >
                          {/* Collapse affordance stacked above the resize grip. */}
                          {this.renderEdgeCollapse()}
                          <KuikaButton
                            data-testid="leftPanelResizeGrip"
                            type="secondary"
                            size="md"
                            isIcon
                            style={{ boxShadow: "var(--shadow-sm)" }}
                          >
                            <KuikaSvg name="arrows-horizontal" type="common" width={16} height={16} />
                          </KuikaButton>
                        </div>
                      )
                    }
                  : undefined
              }
              onResizeStart={this.handleResizeStart}
              onResize={this.handleResize}
              onResizeStop={this.handleResizeStop}
            >
              {/* Views that collapse but don't resize (Report Designer, Process Automation,
                  API, Scheduled Jobs, …) can't hang the affordance off a resize handle that
                  re-resizable never renders, so it gets a handle of its own. */}
              {!supportsLeftPanelResize(this.props.sd.dashboardState) && (
                <div className="kuika-left-panel__resize-handle kuika-left-panel__resize-handle--collapse-only">
                  {this.renderEdgeCollapse()}
                </div>
              )}
              <ManagementLeftPanel />

              {this.props.sd.getVisibility(ViewPart.ScreensPanel) === Visibility.Visible && (
                <ScreensWindow panelName="Screen List" designType="screen" />
              )}
              {this.props.sd.getVisibility(ViewPart.ReportDesignerPagesPanel) === Visibility.Visible && (
                <ScreensWindow panelName="Report" designType="report" />
              )}
              {this.props.sd.getVisibility(ViewPart.ComponentsPanel) === Visibility.Visible && <ComponentList />}
              {this.props.sd.getVisibility(ViewPart.DisplayItemTreePanel) === Visibility.Visible && <ComponentTree />}
              {this.props.sd.getVisibility(ViewPart.DatasourcesPanel) === Visibility.Visible && <DataSources />}
              {this.props.sd.getVisibility(ViewPart.DataDiagramSchemePanel) === Visibility.Visible && (
                <DataDiagramSchemeWindow />
              )}
              {this.props.sd.getVisibility(ViewPart.ActionTreePanel) === Visibility.Visible && <ActionTreePanel />}
              {this.props.sd.getVisibility(ViewPart.WorkFlowFieldListPanel) === Visibility.Visible && (
                <WorkFlowFieldsPanel />
              )}
              {this.props.sd.getVisibility(ViewPart.ZendeskPanel) === Visibility.Visible && <ZendeskPanel />}
              {/* <ImageLibraryWindow /> */}
              {this.props.sd.getVisibility(ViewPart.LanguagePanel) === Visibility.Visible && <LanguageWindow />}
              {this.props.sd.getVisibility(ViewPart.Audit) === Visibility.Visible && <AuditWindow />}
              {this.props.sd.getActiveLeftPanel() === LeftPanelType.Comments && <CommentsPanel />}
            </Resizable>
          </>
        )}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const designLeftPanel = withContext<IDesignLeftPanelProps>(withSD<IDesignLeftPanelProps>(DesignLeftPanel));
export { designLeftPanel as DesignLeftPanel };
